import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PassportCard as Card } from '../../services/api/passportCards';
import type { CollectorActions } from './CollectorActions';
import PassportCard from './PassportCard';
import './collector-card.css';

/** One source/edition, one stable hinge. Navigation and turning are sibling controls. */
export default function CollectorCard({
  card,
  onOwner,
  onRecognition,
  href,
  onOpen,
  openLabel,
  fullRecordExtension,
  actions,
  arrival = false,
  quickReveal = false,
  className = '',
}: {
  card: Card;
  onOwner?: () => void;
  onRecognition?: () => void;
  arrival?: boolean;
  quickReveal?: boolean;
  href?: string;
  onOpen?: () => void;
  openLabel?: string;
  fullRecordExtension?: React.ReactNode;
  actions?: Omit<CollectorActions, 'source'>;
  className?: string;
}) {
  const [interacted, setInteracted] = useState(false);
  const [back, setBack] = useState(false);
  useEffect(() => {
    if (!arrival || interacted) return;
    const timer = setTimeout(() => setBack(true), quickReveal ? 0 : 140);
    return () => clearTimeout(timer);
  }, [arrival, quickReveal, interacted]);
  const label =
    openLabel || `View ${card.subject_type === 'vehicle' ? 'build' : card.subject_type}`;
  const confirmedArrival = arrival && Boolean(card.earned_at || card.ownership_status === 'active');
  const details =
    fullRecordExtension || arrival ? (
      <section className="collector-card__record-details">
        {confirmedArrival && <span className="pc-arrival-caption">Kept in your Passport</span>}
        {fullRecordExtension}
      </section>
    ) : undefined;
  return (
    <div
      className={`collector-card ${arrival ? 'pc-arrival' : ''} ${className}`}
      data-entity-kind={card.subject_type}
      data-quiet={quickReveal || undefined}
    >
      <div className={`collector-card__stage ${arrival ? 'pc-arrival-card' : ''}`}>
        <PassportCard
          card={card}
          onOwner={onOwner}
          onRecognition={onRecognition}
          back={back}
          quiet
          loadBuildDetails={false}
          reduceMotion={arrival && quickReveal && !interacted}
          readableStory
          imageAlt={card.title}
          frontArtAction={
            href ? (
              <Link
                to={href}
                onClick={onOpen}
                className="pc-card-art-open"
                aria-label={`Open ${card.title}`}
              />
            ) : onOpen ? (
              <button
                type="button"
                onClick={onOpen}
                className="pc-card-art-open"
                aria-label={`Open ${card.title}`}
              />
            ) : undefined
          }
          fullRecordExtension={details}
          actions={{ ...actions, source: href || onOpen ? { href, onOpen, label } : undefined }}

          onFlip={() => {
            setInteracted(true);
            setBack((value) => !value);
          }}
        />
      </div>
    </div>
  );
}
